import { canv } from "@/canv/global";
import { generateUID, getBoolProperty, getNumberProperty, getStringProperty } from "@/canv/utils";
import { ElementBase } from "@/elementBase";
import { Group } from "@/group";
import { t } from "@/i18n";
import { selectionManager } from "@/tools/cursorTool";
import { widgetOption } from "@/widgetMenu/widgetMenu";
import { frameWidget } from "../frame";
import {
    BASE_ADD_ICON_SIZE,
    BASE_GAP,
    BASE_HEIGHT,
    BASE_PADDING,
    BASE_SIDE_ADD_ICON_PLUS_SIZE,
    BASE_SIDE_ADD_ICON_SIZE,
    BASE_WIDTH,
} from "./constants";
import type {
    PresentationDirection,
    PresentationLastHoverSideAddIcon,
    PresentationSlide,
    UpdateSlidesProps,
} from "./types";

export class PresentationWidget extends ElementBase {
    constructor(startX: number, startY: number, width = BASE_WIDTH, height = BASE_HEIGHT) {
        super();
        this.startX = startX;
        this.startY = startY;
        this.width = width;
        this.height = height;

        this.updateDims();
    }

    updateDims() {
        this.updateBase(this.startX, this.startY, this.width, this.height);
    }

    // метод по обновлению/удалению слайдов
    updateSlides(data: UpdateSlidesProps) {
        this.fixState("update");

        if ("slide" in data) {
            // добавление слайда
            const slides = [...this.slides];
            slides.splice(data.index, 0, data.slide);
            this.slides = slides;
        } else {
            // удаление слайда
            this.slides = this.slides.filter((_, index) => index !== data.slideIndex);

            if (!this.slides.length) {
                this.setPadding(BASE_PADDING);
            }
        }

        if (this.slides.length <= 1) {
            this.direction = null;
        }

        this.saveState("update");
    }

    reorderSlides(newFramesOrder: frameWidget[]) {
        if (!newFramesOrder.length) return;

        canv.eventManager.openRecord();
        this.fixState("reorderSlides");
        const newSlides: PresentationSlide[] = [];

        for (let i = 0; i < newFramesOrder.length; i++) {
            const frame = newFramesOrder[i];
            const group = frame.parent;

            const existingSlide = this.slides.find((slide) => slide.frameUID === group.UID);

            if (!existingSlide) continue;

            newSlides.push({
                ...existingSlide,
                order: i,
            });
        }
        this.slides = newSlides;

        this.saveState("reorderSlides");

        this.slideAligment();

        canv.eventManager.closeRecord();
    }

    // метод по добавлению слайда
    addSlide(frame?: frameWidget, index?: number) {
        let slideFrame = frame;
        let slideFrameParent = frame?.parent;

        // значит мы кликнули на кнопку "добавить слайд" и создаем новый фрейм
        if (!slideFrame) {
            // если это первый слайд то мы создаем новый фрейм по размерам презентации
            if (!this.slides.length) {
                slideFrame = new frameWidget(
                    this.startX + this.padding,
                    this.startY + this.padding,
                    this.width - this.padding * 2,
                    this.height - this.padding * 2,
                );
            } else {
                // если это не первый слайд то берем предыдущий и по нему создаем новый
                const prevIndex = index !== undefined ? index : this.slides.length - 1;
                const group = canv.canvasController.FindByUID(
                    this.slides[prevIndex].frameUID,
                ) as Group;

                if (group) {
                    const startX =
                        this.direction === "horizontal"
                            ? group.startX + group.width + this.gap
                            : this.startX + this.padding;
                    const startY =
                        this.direction === "vertical"
                            ? group.startY + group.height + this.gap
                            : this.startY + this.padding;
                    const width =
                        this.direction === "horizontal"
                            ? (this.height / BASE_HEIGHT) * BASE_WIDTH - this.padding * 2
                            : this.width - this.padding * 2;
                    const height =
                        this.direction === "vertical"
                            ? group.height
                            : this.height - this.padding * 2;

                    slideFrame = new frameWidget(startX, startY, width, height);
                }
            }

            slideFrameParent = new Group(true);
            slideFrame?.setParent(slideFrameParent);

            if (!slideFrame || !slideFrameParent) {
                throw new Error("Нет фрейма для создания слайда");
            }
        }

        canv.eventManager.openRecord();

        slideFrame.fastAction = "";
        // тут делаем через присваивание для того, что бы нормально отрабатывало дейтвие "назад"
        slideFrame.presentationUID = this.UID;

        if (frame === undefined) {
            // добавляем группу в список только если мы создаем новую
            canv.canvasController.addGroup(slideFrameParent as Group);
            canv.eventManager.createBuildEvent("create", slideFrameParent);
        }

        this.updateSlides({
            slide: {
                UID: generateUID(),
                frameUID: (slideFrameParent as Group).UID,
                order: index ?? this.slides.length + 1,
                name: t("presentations.slideWithNumber", {
                    number: this.slides.length + 1,
                }),
            },
            index: index !== undefined ? index + 1 : this.slides.length + 1,
        });

        this.updateOrderSlides();
        this.slideAligment();

        // при добавлении слайда (> 1) обязательно обновляем размеры презентации
        if (this.slides.length > 1) {
            this.updateSizeBySlides();
        }

        canv.eventManager.closeRecord();
    }

    deleteSlide(frame: frameWidget) {
        // находим индекс слайда который удаляем
        const group = frame.parent;
        const slideIndex = this.slides.findIndex((slide) => slide.frameUID === group.UID);
        if (slideIndex === -1) return;

        const oldDirection = this.direction;
        // удаляем слайд и обновляем позицию следующих
        this.updateSlides({ slideIndex });

        this.slideAligment(oldDirection);

        if (this.slides.length > 0) {
            // ресайзим (уменьшаем) презентацию
            this.updateSizeBySlides();
        }

        // Обновляем индексы слайдов
        this.updateOrderSlides();
    }

    // тут обновляем размеры презентации пробегаясь по всем слайдам и высчитывая
    // полные размеры внутреннего контента презентации
    // нужно для корректировки размеров презентации при удалении или добавлении слайдов
    updateSizeBySlides() {
        let startXContent: number | null = null;
        let startYContent: number | null = null;
        let endXContent: number | null = null;
        let endYContent: number | null = null;

        for (let i = 0; i < this.slides.length; i++) {
            const group = canv.canvasController.FindByUID(this.slides[i].frameUID) as Group;

            if (!group) continue;

            if (startXContent === null || group.startX < startXContent)
                startXContent = group.startX;
            if (startYContent === null || group.startY < startYContent)
                startYContent = group.startY;
            if (endXContent === null || group.startX + group.width > endXContent)
                endXContent = group.startX + group.width;
            if (endYContent === null || group.startY + group.height > endYContent)
                endYContent = group.startY + group.height;
        }

        if (
            startXContent === null ||
            startYContent === null ||
            endXContent === null ||
            endYContent === null
        ) {
            return;
        }

        this.fixState("resize");
        this.width = endXContent - startXContent + this.padding * 2;
        this.height = endYContent - startYContent + this.padding * 2;
        this.saveState("resize");

        this.fixState("move");
        this.startX = startXContent - this.padding;
        this.startY = startYContent - this.padding;
        this.saveState("move");

        this.updateDims();
    }

    // Принимает смещение по оси x и y и индекс слайда с которого начинаем перемещать
    // Нужно для корректироваки слайдов следующих после вставляемого или удаляемого
    slideAligment(direction = this.direction) {
        if (!this.slides.length) {
            return;
        }

        let xCursor = this.startX + this.padding;
        let yCursor = this.startY + this.padding;

        this.translatingStart();
        for (let i = 0; i < this.slides.length; i++) {
            const group = canv.canvasController.FindByUID(this.slides[i].frameUID) as Group;

            if (!group) continue;

            const frame = group.onlyChild() as frameWidget;

            if (!frame) continue;
            const dx = xCursor - group.startX;
            const dy = yCursor - group.startY;

            frame.fixState("move");
            frame.isProgrammaticMove = true;
            group.translateGroup(dx, dy);
            frame.isProgrammaticMove = false;
            frame.saveState("move");

            if (direction === "horizontal") {
                xCursor += group.width + this.gap;
            } else if (direction === "vertical") {
                yCursor += group.height + this.gap;
            }
        }

        this.translatingStop();
    }

    translateSlideAligment() {
        let xCursor = this.startX + this.padding;
        let yCursor = this.startY + this.padding;

        for (let i = 0; i < this.slides.length; i++) {
            const group = canv.canvasController.FindByUID(this.slides[i].frameUID) as Group;

            if (!group) continue;

            const frame = group.onlyChild() as frameWidget;

            const dx = xCursor - group.startX;
            const dy = yCursor - group.startY;

            frame.isProgrammaticMove = true;
            group.translateGroup(dx, dy);
            frame.isProgrammaticMove = false;

            if (this.direction === "horizontal") {
                xCursor += group.width + this.gap;
            } else if (this.direction === "vertical") {
                yCursor += group.height + this.gap;
            }
        }
    }

    // метод по обновлению порядка слайдов
    updateOrderSlides() {
        this.fixState("reorderSlides");

        for (let i = 0; i < this.slides.length; i++) {
            this.slides[i].order = i;
        }

        this.saveState("reorderSlides");
    }

    // метод по изменению отступов
    setPadding(padding: number) {
        this.padding = padding;

        this.setGap();
    }

    setGap() {
        this.gap = this.padding * (BASE_GAP / BASE_PADDING);
    }

    draw() {
        canv.ctx.save();

        this.drawSubstrateRect();
        this.drawEmpty();
        this.drawSideAddIcons();
        this.drawSlidesIndex();

        canv.ctx.restore();
    }

    drawSlidesIndex() {
        const scale =
            this.direction === "vertical" ? this.width / BASE_WIDTH : this.height / BASE_HEIGHT;

        canv.ctx.font = `400 ${14 * scale}px 'Open Sans', sans-serif`;
        canv.ctx.fillStyle = "#7C7C91";

        for (let i = 0; i < this.slides.length; i++) {
            const slideGroup = canv.canvasController.FindByUID(this.slides[i].frameUID) as Group;

            if (!slideGroup) continue;

            const metrics = canv.ctx.measureText(String(i + 1));
            const actualHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

            const paddingY = 7.5 * scale;

            canv.ctx.fillText(
                String(i + 1),
                slideGroup.startX + metrics.width,
                slideGroup.startY + slideGroup.height + paddingY + actualHeight,
            );
        }
    }

    drawEmpty() {
        if (this.slides.length) return;

        canv.ctx.strokeStyle = "rgba(225, 224, 231, 1)";
        canv.ctx.setLineDash([5, 1]);
        canv.ctx.beginPath();
        canv.ctx.roundRect(
            this.startX + this.padding,
            this.startY + this.padding,
            this.width - this.padding * 2,
            this.height - this.padding * 2,
            canv.actionManager.getZoomConstant(4),
        );

        canv.ctx.stroke();
        canv.ctx.setLineDash([]);

        const scale = this.height / BASE_HEIGHT;

        const circleDiameter = BASE_ADD_ICON_SIZE * scale;
        const circleRadius = circleDiameter / 2;

        const centerX = this.startX + 396 * scale + circleRadius;
        const centerY = this.startY + 192 * scale + circleRadius;

        const gapPx = 16 * scale;
        const fontSize = scale * 16;

        const isHoverPlus = this.isOverPlus();

        canv.ctx.beginPath();
        canv.ctx.fillStyle = isHoverPlus ? "rgba(240, 240, 243, 1)" : "rgba(245, 245, 247, 1)";

        canv.ctx.arc(centerX, centerY, circleRadius, 0, 2 * Math.PI);
        canv.ctx.fill();

        const plusSize = circleDiameter * 0.32;
        const plusHalf = plusSize / 2;

        canv.ctx.beginPath();
        canv.ctx.lineCap = "round";
        canv.ctx.strokeStyle = isHoverPlus ? "rgba(44, 45, 50, 1)" : "rgba(124, 124, 145, 1)";
        canv.ctx.lineWidth = 1.5 * scale;
        canv.ctx.moveTo(centerX - plusHalf, centerY);
        canv.ctx.lineTo(centerX + plusHalf, centerY);
        canv.ctx.moveTo(centerX, centerY - plusHalf);
        canv.ctx.lineTo(centerX, centerY + plusHalf);
        canv.ctx.stroke();

        canv.ctx.textAlign = "center";
        canv.ctx.textBaseline = "top";

        canv.ctx.fillStyle = "rgba(44, 45, 50, 1)";
        canv.ctx.font = `${fontSize}px sans-serif`;
        const text1Y = centerY + circleRadius + gapPx;
        canv.ctx.fillText(t("presentations.new"), centerX, text1Y);

        canv.ctx.fillStyle = "rgba(124, 124, 145, 1)";
        canv.ctx.font = `${fontSize}px sans-serif`;
        const text2Y = text1Y + fontSize + gapPx;
        canv.ctx.fillText(t("presentations.emptyDropFrames"), centerX, text2Y);
        canv.ctx.fillText(t("presentations.emptyOrCreateSlide"), centerX, text2Y + 20 * scale);
    }

    drawSubstrateRect() {
        canv.ctx.beginPath();

        canv.ctx.lineWidth = canv.actionManager.getZoomConstant(1);
        canv.ctx.fillStyle = "white";
        canv.ctx.strokeStyle = "rgba(240, 240, 243, 1)";

        this.drawRoundedRect(
            this.startX,
            this.startY,
            this.width,
            this.height,
            canv.actionManager.getZoomConstant(4),
        );

        canv.ctx.fill();
        canv.ctx.stroke();
    }

    // метод отрисовки боковых иконок добавления новых слайдов
    // TODO: тут будет подсветка при наведении фрейма DnD
    drawSideAddIcons() {
        if (!this.slides.length || !this.isOver()) return;
        const pt = canv.actionManager.getActionPoint();

        for (let i = 0; i < this.slides.length; i++) {
            const group = canv.canvasController.FindByUID(this.slides[i].frameUID) as Group;
            const isLast = i === this.slides.length - 1;

            const scale = isLast ? this.padding / BASE_PADDING : this.gap / BASE_GAP;
            const size = BASE_SIDE_ADD_ICON_SIZE * scale;

            if (!group) continue;

            const isHoverVertical =
                pt.y > group.startY + group.height &&
                pt.y < group.startY + group.height + (isLast ? this.padding : this.gap);
            const isHoverHorizontal =
                pt.x > group.startX + group.width &&
                pt.x < group.startX + group.width + (isLast ? this.padding : this.gap);

            if (this.direction !== "vertical" && isHoverHorizontal) {
                this.hoverDirection = "horizontal";

                const startX =
                    group.startX + group.width + ((isLast ? this.padding : this.gap) - size) / 2;
                const startY = this.startY + this.height / 2 - size / 2;

                this.drawSideAddIcon(startX, startY, i, isLast);
            }

            if (this.direction !== "horizontal" && isHoverVertical) {
                this.hoverDirection = "vertical";

                const startX = this.startX + this.width / 2 - size / 2;
                const startY =
                    group.startY + group.height + ((isLast ? this.padding : this.gap) - size) / 2;

                this.drawSideAddIcon(startX, startY, i, isLast);
            }
        }
    }

    drawSideAddIcon(startX: number, startY: number, index: number, isLast: boolean) {
        canv.ctx.beginPath();

        const pt = canv.actionManager.getActionPoint();
        const scale = isLast ? this.padding / BASE_PADDING : this.gap / BASE_GAP;
        const size = BASE_SIDE_ADD_ICON_SIZE * scale;

        const isHoverPlus =
            pt.x >= startX && pt.x <= startX + size && pt.y >= startY && pt.y <= startY + size;

        this.lastHoverSideAddIcon = {
            startX,
            startY,
            width: size,
            height: size,
            index,
        };

        if (
            isHoverPlus &&
            !this._isHoveringAddIcon &&
            canv.toolBox.currentTool?.name !== "dragTool"
        ) {
            this._isHoveringAddIcon = true;
            canv.canvas.style.cursor = "pointer";
        } else if (!isHoverPlus && this._isHoveringAddIcon) {
            this._isHoveringAddIcon = false;
            canv.canvas.style.cursor = "";
        }

        canv.ctx.fillStyle = isHoverPlus ? "rgba(240, 240, 243, 1)" : "rgba(245, 245, 247, 1)";
        canv.ctx.strokeStyle = isHoverPlus ? "rgba(44, 45, 50, 1)" : "rgba(124, 124, 145, 1)";

        canv.ctx.roundRect(startX, startY, size, size, canv.actionManager.getZoomConstant(4));

        canv.ctx.fill();
        canv.ctx.beginPath();
        canv.ctx.lineCap = "round";
        canv.ctx.lineWidth = 1.5 * scale;

        const padding = ((BASE_SIDE_ADD_ICON_SIZE - BASE_SIDE_ADD_ICON_PLUS_SIZE) * scale) / 2;

        canv.ctx.moveTo(startX + size / 2, startY + padding);
        canv.ctx.lineTo(startX + size / 2, startY + size - padding);
        canv.ctx.moveTo(startX + padding, startY + size / 2);
        canv.ctx.lineTo(startX + size - padding, startY + size / 2);

        canv.ctx.stroke();
        canv.ctx.closePath();
    }

    drawRoundedRect(x: number, y: number, w: number, h: number, r: number) {
        if (w < 0 || h < 0 || r < 0) {
            return;
        }
        let radius = r;
        if (w < 2 * r) radius = w / 2;
        if (h < 2 * r) radius = h / 2;
        canv.ctx.beginPath();
        canv.ctx.moveTo(x + radius, y);
        canv.ctx.arcTo(x + w, y, x + w, y + h, radius);
        canv.ctx.arcTo(x + w, y + h, x, y + h, radius);
        canv.ctx.arcTo(x, y + h, x, y, radius);
        canv.ctx.arcTo(x, y, x + w, y, radius);
        canv.ctx.closePath();
    }

    isOverPlus() {
        if (this.slides.length) return false;

        const pt = canv.actionManager.getActionPoint();

        const scale = this.height / BASE_HEIGHT;

        const circleDiameter = BASE_ADD_ICON_SIZE * scale;
        const circleRadius = circleDiameter / 2;

        const centerX = this.startX + 396 * scale + circleRadius;
        const centerY = this.startY + 192 * scale + circleRadius;

        return (
            pt.x >= centerX - circleRadius &&
            pt.x <= centerX + circleRadius &&
            pt.y >= centerY - circleRadius &&
            pt.y <= centerY + circleRadius
        );
    }

    isOverLastHoverSideAddIcon() {
        if (!this.lastHoverSideAddIcon) return false;

        const pt = canv.actionManager.getActionPoint();

        return (
            pt.x >= this.lastHoverSideAddIcon.startX &&
            pt.x <= this.lastHoverSideAddIcon.startX + this.lastHoverSideAddIcon.width &&
            pt.y >= this.lastHoverSideAddIcon.startY &&
            pt.y <= this.lastHoverSideAddIcon.startY + this.lastHoverSideAddIcon.height
        );
    }

    isInteraction(context: string): { type: string } {
        if (context === "click") {
            // кликнул по плюсу в пустой презентации
            if (this.isOverPlus()) {
                this.addSlide();
            } else if (
                // кликнул по плюсу для добавления слайда
                this.lastHoverSideAddIcon &&
                this.isOverLastHoverSideAddIcon() &&
                this.hoverDirection
            ) {
                this.direction = this.hoverDirection;

                this.addSlide(undefined, this.lastHoverSideAddIcon.index);
                this.lastHoverSideAddIcon = null;
            }
        }

        return { type: "none" };
    }

    translatingStart() {
        // нужно для того, что бы элементы на фрейме тоже передвигались вместе с ним
        for (let i = 0; i < this.slides.length; i++) {
            const frameGroup = canv.canvasController.FindByUID(this.slides[i].frameUID) as Group;
            if (!frameGroup) continue;
            frameGroup.fixWidgets("move");

            frameGroup.groupTranslatingStart();
        }
    }

    translatingStop() {
        // нужно для того, что бы элементы на фрейме тоже передвигались вместе с ним
        for (let i = 0; i < this.slides.length; i++) {
            const frameGroup = canv.canvasController.FindByUID(this.slides[i].frameUID) as Group;
            if (!frameGroup) continue;
            frameGroup.saveWidgets("move");

            frameGroup.groupTranslatingStop();
        }
    }

    translate(x: number, y: number) {
        this.startX += x;
        this.startY += y;

        this.updateDims();
        this.translateSlideAligment();
    }

    resizingStart() {
        // фиксируем paddings для презентации
        this.fixState("update");

        for (let i = 0; i < this.slides.length; i++) {
            const group = canv.canvasController.FindByUID(this.slides[i].frameUID) as Group;

            if (!group) continue;

            const frame = group.onlyChild() as frameWidget;

            // для каждого фрейма фиксируем его место расположения
            frame.fixState("move");
            group.groupTranslatingStart();
            frame.resizingStart();

            // для каждого фрейма фиксируем его размеры
            frame.fixState("resize");
        }
    }

    resizingStop() {
        // сохраняем новый padding для презентации
        this.saveState("update");

        for (let i = 0; i < this.slides.length; i++) {
            const group = canv.canvasController.FindByUID(this.slides[i].frameUID) as Group;

            if (!group) continue;

            const frame = group.onlyChild() as frameWidget;

            // для каждого фрейма сохраняем его расположение
            frame.resizingStop();
            group.groupTranslatingStop();
            frame.saveState();

            // и размеры
            frame.saveState("resize");
        }
    }

    resize(x: number, y: number, catchPointer: string) {
        if (catchPointer !== "bottomRight") return;

        const diff = Math.abs(x) > Math.abs(y) ? x : y;

        if (this.height + diff <= 15) return;

        const widthKoef = this.width / this.height;
        const paddingDiff = diff * (this.padding / this.height);
        const scaleWidth = (widthKoef * diff) / this.width;
        const scaleHeight = diff / this.height;

        const newWidth = this.width + widthKoef * diff;
        const newHeight = this.height + diff;
        const newPadding = this.padding + paddingDiff;

        const someOneCanResize = this.slides.length
            ? this.slides.some((slide) => {
                  const group = canv.canvasController.FindByUID(slide.frameUID) as Group;

                  if (!group) return false;

                  const frame = group.onlyChild() as frameWidget;

                  if (!frame) return false;

                  return frame.canResize(group.width * scaleWidth, group.height * scaleHeight);
              })
            : true;

        if (!someOneCanResize) return;

        // обновляем размеры презентации
        this.height = newHeight;
        this.width = newWidth;

        this.updateDims();

        // обновляем padding презентации
        this.setPadding(newPadding);

        let xMove = this.startX + this.padding;
        let yMove = this.startY + this.padding;

        for (let i = 0; i < this.slides.length; i++) {
            const group = canv.canvasController.FindByUID(this.slides[i].frameUID) as Group;

            if (!group) continue;

            const frame = group.onlyChild() as frameWidget;

            // сначала перемещаем слайды на расстояние добавления padding и предыдущих слайдов
            // а так же gap тоже учитываем т.к. он прирастает из-за прироста padding
            group.translateGroup(xMove - group.startX, yMove - group.startY, false, true);

            // равномерно увеличиваем размеры фрейма
            frame.resize(group.width * scaleWidth, group.height * scaleHeight, catchPointer, true);

            if (this.direction === "horizontal") {
                xMove += group.width + this.gap;
            } else if (this.direction === "vertical") {
                yMove += group.height + this.gap;
            }
        }
    }

    resizeFromFrame(
        changedFrame: frameWidget,
        newFrameWidth: number,
        newFrameHeight: number,
        oldFrameWidth: number,
        oldFrameHeight: number,
    ) {
        const slideIndex = this.slides.findIndex(
            (slide) => slide.frameUID === changedFrame.parentUID,
        );

        if (slideIndex === -1) return;

        // 1. Пересчёт размера презентации
        if (this.slides.length === 1) {
            this.width = newFrameWidth + this.padding * 2;
            this.height = newFrameHeight + this.padding * 2;
        } else {
            let totalWidth = this.padding * 2;
            let totalHeight = this.padding * 2;

            for (let i = 0; i < this.slides.length; i++) {
                const group = canv.canvasController.FindByUID(this.slides[i].frameUID) as Group;
                if (!group) continue;

                const w = group.UID === changedFrame.parentUID ? newFrameWidth : group.width;
                const h = group.UID === changedFrame.parentUID ? newFrameHeight : group.height;

                if (this.direction === "horizontal") {
                    totalWidth += w + (i < this.slides.length - 1 ? this.gap : 0);
                    totalHeight = Math.max(totalHeight, h + this.padding * 2);
                } else if (this.direction === "vertical") {
                    totalHeight += h + (i < this.slides.length - 1 ? this.gap : 0);
                    totalWidth = Math.max(totalWidth, w + this.padding * 2);
                }
            }

            this.width = totalWidth;
            this.height = totalHeight;
        }

        this.updateDims();

        // 2. Сдвигаем слайды после изменённого — используем старые размеры для дельты
        if (this.slides.length > 1 && slideIndex < this.slides.length - 1) {
            const deltaX = this.direction === "horizontal" ? newFrameWidth - oldFrameWidth : 0;
            const deltaY = this.direction === "vertical" ? newFrameHeight - oldFrameHeight : 0;

            if (deltaX !== 0 || deltaY !== 0) {
                for (let i = slideIndex + 1; i < this.slides.length; i++) {
                    const group = canv.canvasController.FindByUID(this.slides[i].frameUID) as Group;
                    if (!group) continue;

                    const frame = group.onlyChild() as frameWidget;

                    frame.isProgrammaticMove = true;
                    group.translateGroup(deltaX, deltaY);
                    frame.isProgrammaticMove = false;
                }
            }
        }

        this.scaleFrameChildren(
            changedFrame,
            newFrameWidth,
            newFrameHeight,
            oldFrameWidth,
            oldFrameHeight,
        );
    }

    scaleFrameChildren(
        changedFrame: frameWidget,
        newFrameWidth: number,
        newFrameHeight: number,
        oldFrameWidth: number,
        oldFrameHeight: number,
    ) {
        if (this.isScaling) return;
        if (oldFrameWidth === 0 || oldFrameHeight === 0) return;
        this.isScaling = true;

        try {
            const scaleX = newFrameWidth / oldFrameWidth;
            const scaleY = newFrameHeight / oldFrameHeight;

            const anchorX = changedFrame.startX;
            const anchorY = changedFrame.startY;

            const itemsToScale: {
                item: Group;
                child: ElementBase & {
                    startX: number;
                    startY: number;
                    width: number;
                    height: number;
                };
                origStartX: number;
                origStartY: number;
                origWidth: number;
                origHeight: number;
            }[] = [];

            for (const item of canv.canvasController.items) {
                if (item.UID === changedFrame.parentUID) continue;

                const child = item.onlyChild() as ElementBase & {
                    startX: number;
                    startY: number;
                    width: number;
                    height: number;
                };
                if (!child) continue;

                const insideFrame =
                    child.startX >= anchorX - 1 &&
                    child.startY >= anchorY - 1 &&
                    child.startX + child.width <= anchorX + oldFrameWidth + 1 &&
                    child.startY + child.height <= anchorY + oldFrameHeight + 1;

                if (!insideFrame) continue;

                itemsToScale.push({
                    item,
                    child,
                    origStartX: child.startX,
                    origStartY: child.startY,
                    origWidth: child.width,
                    origHeight: child.height,
                });
            }

            for (const {
                item,
                child,
                origStartX,
                origStartY,
                origWidth,
                origHeight,
            } of itemsToScale) {
                const xMove = origWidth * scaleX - origWidth;
                const yMove = origHeight * scaleY - origHeight;

                const newStartX = anchorX + (origStartX - anchorX) * scaleX;
                const newStartY = anchorY + (origStartY - anchorY) * scaleY;

                item.resizeGroup(xMove, yMove, "bottomRight", true);
                item.translateGroup(newStartX - child.startX, newStartY - child.startY, true, true);
            }
        } finally {
            this.isScaling = false;
        }
    }

    regenerateUIDS() {
        this.slides = this.slides.map((slide) => {
            return {
                ...slide,
                UID: generateUID(),
            };
        });
    }

    getWidgetOptions() {
        const widgetOptions: widgetOption[] = [];
        widgetOptions.push(
            new widgetOption(
                "presentationName",
                this.name || t("presentations.untitled"),
                this.UID,
            ),
        );
        if (this.slides.length && canv.isBoardModeEditOrOwner()) {
            widgetOptions.push(new widgetOption("presentationPlay", null, this.UID));
        }

        return widgetOptions;
    }

    play() {
        this.fixState("startPresentation");

        const token = canv.getInitDataProp("userToken");
        canv.eventManager.openRecord();
        this.presenterToken = token;
        this.zoom = 100;
        this.currentSlide = 0;
        this.isActive = true;
        this.viewerUIDs = [token];

        this.saveState("startPresentation");
        canv.eventManager.closeRecord();

        selectionManager.setSelected([], true);
    }

    stop() {
        this.fixState("stopPresentation");

        canv.eventManager.openRecord();
        this.zoom = 100;
        this.presenterToken = "";
        this.currentSlide = 0;
        this.isActive = false;
        this.viewerUIDs = [];

        this.saveState("stopPresentation");
        canv.eventManager.closeRecord();
    }

    setName(name: string) {
        this.fixState("update");
        canv.eventManager.openRecord();
        this.name = name;
        this.saveState("update");
        canv.eventManager.closeRecord();
    }

    setZoom(zoom: number) {
        let newZoom = zoom;
        if (newZoom < 1) newZoom = 1;
        if (newZoom > 200) newZoom = 200;
        this.fixState("zoomPresentation");

        canv.eventManager.openRecord();
        this.zoom = newZoom;

        this.saveState("zoomPresentation");
        canv.eventManager.closeRecord();
    }

    setCurrentSlide(index: number) {
        let newIndex = index;
        if (newIndex < 0) newIndex = 0;
        if (newIndex >= this.slides.length - 1) newIndex = this.slides.length - 1;
        this.fixState("navigatePresentation");

        canv.eventManager.openRecord();
        this.currentSlide = newIndex;

        this.saveState("navigatePresentation");
        canv.eventManager.closeRecord();
    }

    join() {
        canv.intercom.eventMessage([
            {
                type: "property",
                UID: this.UID,
                fixType: "joinPresentation",
                state: {
                    viewerUID: canv.getInitDataProp("userToken"),
                },
            },
        ]);
    }
    leave() {
        canv.intercom.eventMessage([
            {
                type: "property",
                UID: this.UID,
                fixType: "leave",
                state: {
                    viewerUID: canv.getInitDataProp("userToken"),
                },
            },
        ]);
    }

    getFixType(reason: string) {
        switch (reason) {
            case "reorderSlides":
                return "reorderSlides";
            case "update":
                return "update";
            case "move":
                return "move";
            case "resize":
                return "resize";
            case "startPresentation":
                return "startPresentation";
            case "stopPresentation":
                return "stopPresentation";
            case "zoomPresentation":
                return "zoomPresentation";
            case "navigatePresentation":
                return "navigatePresentation";
            default:
                return "full";
        }
    }

    getState(fixType: string) {
        switch (fixType) {
            case "reorderSlides":
                return {
                    slides: this.slides,
                    entityType: "presentationWidget",
                };
            case "update":
                return {
                    slides: this.slides,
                    name: this.name,
                    padding: this.padding,
                    direction: this.slides.length <= 1 ? null : this.direction,
                    entityType: "presentationWidget",
                };
            case "move":
                return {
                    startX: this.startX,
                    startY: this.startY,
                };
            case "resize":
                return {
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                };
            case "startPresentation":
                return {
                    zoom: this.zoom,
                    presenterToken: this.presenterToken,
                    currentSlide: this.currentSlide,
                    isActive: this.isActive,
                    viewerUIDs: this.viewerUIDs,
                };
            case "stopPresentation":
                return {
                    zoom: this.zoom,
                    presenterToken: this.presenterToken,
                    currentSlide: this.currentSlide,
                    isActive: this.isActive,
                    viewerUIDs: this.viewerUIDs,
                };
            case "zoomPresentation":
                return {
                    zoom: this.zoom,
                };
            case "navigatePresentation":
                return {
                    currentSlide: this.currentSlide,
                };
            default:
                return {
                    UID: this.UID,
                    parentUID: this.parentUID,
                    entityType: "presentationWidget",
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                    slides: this.slides,
                    creatorToken: this.creatorToken,
                    presenterToken: this.presenterToken,
                    currentSlide: this.currentSlide,
                    isActive: this.isActive,
                    viewerUIDs: this.viewerUIDs,
                    name: this.name,
                    accountName: this.accountName,
                    createdAt: this.createdAt,
                    updatedAt: this.updatedAt,
                    direction: this.direction,
                    padding: this.padding,
                    zoom: this.zoom,
                };
        }
    }

    loadState(stateObj: PresentationWidget, fixType: string): void {
        if (fixType === "full") {
            const state = stateObj;

            this.UID = getStringProperty("UID", state);
            this.parentUID = getStringProperty("parentUID", state);
            this.startX = getNumberProperty("startX", state);
            this.startY = getNumberProperty("startY", state);
            this.width = getNumberProperty("width", state);
            this.height = getNumberProperty("height", state);
            this.slides = state.slides || this.slides;
            this.name = state.name || this.name; // бэк сам валидирует name, верим что он всегда присылает безопасное значение
            this.creatorToken = getStringProperty("creatorToken", state);
            this.presenterToken = getStringProperty("presenterToken", state);
            this.currentSlide = getNumberProperty("currentSlide", state);
            this.isActive = getBoolProperty("isActive", state);
            this.viewerUIDs = state.viewerUIDs.map((_, i) =>
                getStringProperty(i.toString(), state.viewerUIDs),
            );
            this.accountName = getStringProperty("accountName", state);
            this.createdAt = getStringProperty("createdAt", state);
            this.updatedAt = getStringProperty("updatedAt", state);
            this.direction = state.direction || null;
            this.setPadding(getNumberProperty("padding", state));
            this.zoom = getNumberProperty("zoom", state);
        } else if (fixType === "move") {
            const state = stateObj;

            this.startX = getNumberProperty("startX", state);
            this.startY = getNumberProperty("startY", state);
        } else if (fixType === "resize") {
            const state = stateObj;

            this.startX = getNumberProperty("startX", state);
            this.startY = getNumberProperty("startY", state);
            this.width = getNumberProperty("width", state);
            this.height = getNumberProperty("height", state);
        } else if (fixType === "update") {
            this.slides = stateObj.slides || this.slides;
            this.name = stateObj.name || this.name;
            this.direction = stateObj.direction || null;
            this.setPadding(getNumberProperty("padding", stateObj));
        } else if (fixType === "reorderSlides") {
            this.slides = stateObj.slides || this.slides;
        } else if (fixType === "startPresentation") {
            const state = stateObj;

            this.presenterToken = getStringProperty("presenterToken", state);
            this.currentSlide = getNumberProperty("currentSlide", state);
            this.isActive = getBoolProperty("isActive", state);
            this.viewerUIDs = state.viewerUIDs.map((_, i) =>
                getStringProperty(i.toString(), state.viewerUIDs),
            );
            this.zoom = getNumberProperty("zoom", state);
        } else if (fixType === "stopPresentation") {
            const state = stateObj;

            this.presenterToken = getStringProperty("presenterToken", state);
            this.currentSlide = getNumberProperty("currentSlide", state);
            this.isActive = getBoolProperty("isActive", state);
            this.viewerUIDs = state.viewerUIDs.map((_, i) =>
                getStringProperty(i.toString(), state.viewerUIDs),
            );
            this.zoom = getNumberProperty("zoom", state);
        } else if (fixType === "zoomPresentation") {
            const state = stateObj;
            this.zoom = getNumberProperty("zoom", state);
        } else if (fixType === "navigatePresentation") {
            const state = stateObj;
            this.currentSlide = getNumberProperty("currentSlide", state);
        } else if (fixType === "joinPresentation") {
            const state = stateObj;
            const uid = getStringProperty("viewerUID", state);
            if (!this.viewerUIDs.includes(uid)) {
                this.viewerUIDs.push(uid);
            }
        } else if (fixType === "leave") {
            const state = stateObj;
            const uid = getStringProperty("viewerUID", state);
            this.viewerUIDs = this.viewerUIDs.filter((item) => item !== uid);
        }

        this.updateDims();
    }

    constructorName: "presentation" = "presentation";

    startX = 0;
    startY = 0;
    width = 0;
    height = 0;
    name = "";
    accountName = "";
    creatorToken = "";
    presenterToken = "";
    currentSlide = 0;
    isActive = false;
    viewerUIDs: string[] = [];
    createdAt: string = "";
    updatedAt: string = "";
    zoom: number = 100;

    padding: number = BASE_PADDING;
    gap: number = BASE_GAP;

    rotatable: boolean = false;

    hoverDirection: PresentationDirection | null = null;
    lastHoverSideAddIcon: PresentationLastHoverSideAddIcon | null = null;

    direction: PresentationDirection | null = null;
    slides: PresentationSlide[] = [];
    viewerUID: string = "";
    fastAction: string = "";
    isScaling = false;
    _isHoveringAddIcon = false;
}
